import {
  getAuthenticatedPathnameFromPublicPath,
  normalizePathname,
  PUBLIC_PUBLISH_ROUTE_PREFIX,
} from '~publish/helpers/publicPublishPaths'
import {
  communityPage,
  createTemplates,
  generic,
  newPost,
  schedule,
  settingsChannelsRoute,
} from '~publish/legacy/routes'

/**
 * Logged-out Publish routing is intentionally narrower than the authenticated
 * router. Public preview routes are product-area landing pages under `/p/*` so
 * they do not shadow the real authenticated app routes.
 *
 * Everything else, including legacy redirects, nested routes, dynamic resource
 * paths, and action-bearing URLs, falls back to a login handoff so the full URL
 * survives authentication.
 */
export type LoggedOutRouteIntent = 'public-preview' | 'private-resource'
export type PublicRouteSection =
  | 'home'
  | 'templates'
  | 'composer'
  | 'publish'
  | 'community'
  | 'insights'
  | 'analytics'
  | 'connect-channels'

export {
  getAuthenticatedPathnameFromPublicPath,
  isPublicPublishPath,
  normalizePathname,
  PUBLIC_PUBLISH_ROUTE_PREFIX,
} from '~publish/helpers/publicPublishPaths'

/**
 * Query params in this set carry post-login action intent. They take precedence
 * over the public route registry because dropping them would turn an action link
 * into a generic public preview.
 */
const AUTH_HANDOFF_QUERY_PARAMS = new Set([
  'connectChannel',
  'channelStorefront',
])

/**
 * Canonical app paths that have public equivalents under `/p/*`. Keep this
 * registry exact; the template preview route is the sole bounded dynamic route.
 * Other nested paths usually include account-specific identity or action state
 * that must survive a login handoff.
 *
 * SECURITY: Each entry permits a page to render without a session under `/p`.
 * Only add pages whose components, data, and actions support anonymous access.
 * This registry is not API authorization. See ./README.md before adding entries.
 */
const PUBLIC_PREVIEW_ROUTE_SECTIONS = new Map<string, PublicRouteSection>([
  [generic.route, 'home'],
  ['/home', 'home'],
  [createTemplates.route, 'templates'],
  [newPost.route, 'composer'],
  [schedule.route, 'publish'],
  [communityPage.route, 'community'],
  ['/insights', 'insights'],
  ['/analyze', 'analytics'],
  [settingsChannelsRoute.route, 'connect-channels'],
])

const PUBLIC_TEMPLATE_DETAILS_PREFIX = `${createTemplates.route}/template/`

function isPublicTemplateDetailsPath(pathname: string): boolean {
  if (!pathname.startsWith(PUBLIC_TEMPLATE_DETAILS_PREFIX)) return false

  const encodedTemplateId = pathname.slice(
    PUBLIC_TEMPLATE_DETAILS_PREFIX.length,
  )
  if (!encodedTemplateId || encodedTemplateId.includes('/')) return false

  try {
    return !decodeURIComponent(encodedTemplateId).includes('/')
  } catch {
    return false
  }
}

export function toPublicPublishPath(pathname: string): string {
  const normalizedPathname = normalizePathname(pathname)

  if (normalizedPathname === generic.route) {
    return PUBLIC_PUBLISH_ROUTE_PREFIX
  }

  return `${PUBLIC_PUBLISH_ROUTE_PREFIX}${normalizedPathname}`
}

/**
 * Only known action params force private handoff. Other query params stay public
 * when the path itself is a prefixed canonical public preview route.
 */
function hasAuthHandoffQueryParam(search: string): boolean {
  const searchParams = new URLSearchParams(search)

  for (const queryParam of AUTH_HANDOFF_QUERY_PARAMS) {
    if (searchParams.has(queryParam)) {
      return true
    }
  }

  return false
}

/**
 * Resolve the public empty-state section for a logged-out location.
 *
 * Decision order:
 * 1. Known action query params force private handoff.
 * 2. Prefixed canonical public paths map to a public preview section.
 * 3. Everything else is private by default.
 */
export function getPublicRouteSection(
  pathname: string,
  search = '',
): PublicRouteSection | null {
  if (hasAuthHandoffQueryParam(search)) {
    return null
  }

  const authenticatedPathname = getAuthenticatedPathnameFromPublicPath(pathname)

  if (!authenticatedPathname) {
    return null
  }

  const exactSection = PUBLIC_PREVIEW_ROUTE_SECTIONS.get(authenticatedPathname)
  if (exactSection) return exactSection

  return isPublicTemplateDetailsPath(authenticatedPathname) ? 'templates' : null
}

/**
 * Public intent helper for callers that only need to choose between the public
 * empty state and the private login handoff.
 */
export function getLoggedOutRouteIntent(
  pathname: string,
  search = '',
): LoggedOutRouteIntent {
  return getPublicRouteSection(pathname, search)
    ? 'public-preview'
    : 'private-resource'
}
